import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  FaBullhorn,
  FaPaperclip,
  FaUsers,
  FaCheck,
  FaExclamationCircle,
  FaCalendarAlt,
  FaUserTie,
  FaTimes,
  FaPlusCircle,
  FaEdit,
  FaAlignLeft,
  FaToggleOn,
  FaStar,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import useAuth from "../../useAuth";
import { server } from "../../server";

export default function NoticeForm({
  isOpen,
  onClose,
  notice,
  noticeToEdit,
  onSuccess,
}) {
  const currentNotice = notice || noticeToEdit;
  const isEdit = !!currentNotice;
  const { user, token } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      Title: "",
      Content: "",
      TargetAudience: "All",
      DeliveredBy: "School Administration",
      ScheduleDate: new Date().toISOString().split("T")[0],
      IsImportant: false,
      IsActive: true,
    },
  });

  const isImportantValue = watch("IsImportant");

  useEffect(() => {
    if (currentNotice) {
      const formattedNotice = { ...currentNotice };
      reset({
        Title: formattedNotice.Title || formattedNotice.title || "",
        Content: formattedNotice.Content || formattedNotice.content || "",
        TargetAudience: formattedNotice.TargetAudience || formattedNotice.targetAudience || "All",
        DeliveredBy: formattedNotice.DeliveredBy || formattedNotice.deliveredBy || "School Administration",
        ScheduleDate: formattedNotice.ScheduleDate
          ? formattedNotice.ScheduleDate.split("T")[0]
          : formattedNotice.scheduleDate
          ? formattedNotice.scheduleDate.split("T")[0]
          : new Date().toISOString().split("T")[0],
        IsImportant: Boolean(formattedNotice.IsImportant || formattedNotice.isImportant),
        IsActive: true,
      });
    } else {
      reset({
        Title: "",
        Content: "",
        TargetAudience: "All",
        DeliveredBy: "School Administration",
        ScheduleDate: new Date().toISOString().split("T")[0],
        IsImportant: false,
        IsActive: true,
      });
    }
  }, [currentNotice, reset, isOpen]);

  if (!isOpen) return null;

  const onSubmit = async (data) => {
    try {
      const url = isEdit
        ? `${server}/notices/${currentNotice.Id || currentNotice.id}`
        : `${server}/notices`;

      const method = isEdit ? "put" : "post";

      const formData = new FormData();
      formData.append("Title", data.Title);
      formData.append("Content", data.Content);
      formData.append("PostedBy", user?.id || 1);
      formData.append("TargetAudience", data.TargetAudience || "All");
      formData.append("IsImportant", data.IsImportant ? "true" : "false");
      formData.append("IsActive", "true");

      if (data.ScheduleDate) formData.append("ScheduleDate", data.ScheduleDate);
      if (data.DeliveredBy) formData.append("DeliveredBy", data.DeliveredBy);

      if (data.AttachmentDocument && data.AttachmentDocument[0]) {
        formData.append("AttachmentFile", data.AttachmentDocument[0]);
      }

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(
        isEdit ? "Notice announcement updated!" : "Notice announcement published!",
      );
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Error publishing notice announcement.",
      );
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/65 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Royal Blue Header Bar */}
        <div className="bg-[#1d5bd8] text-white p-5 sm:p-6 flex items-center justify-between shrink-0 shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white font-black shadow-xs border border-white/20">
              {isEdit ? <FaEdit size={18} /> : <FaBullhorn size={18} />}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-white leading-tight">
                {isEdit ? "Edit Notice Announcement" : "Post New Announcement"}
              </h2>
              <p className="text-xs text-blue-100 font-medium mt-0.5">
                {isEdit
                  ? "Modify existing notice details and audience visibility"
                  : "Publish official school circulars, notices, and events"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-2xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition cursor-pointer"
            aria-label="Close modal"
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Form Fields */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Notice Title */}
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Notice Title <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaBullhorn size={14} />
                </div>
                <input
                  {...register("Title", { required: "Notice title is required" })}
                  className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                    errors.Title
                      ? "border-rose-500 bg-rose-50/50"
                      : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                  } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all`}
                  placeholder="e.g. Annual Sports Meet Schedule & Guidelines"
                />
                {errors.Title && (
                  <div className="absolute right-3 top-3 text-rose-500">
                    <FaExclamationCircle size={14} />
                  </div>
                )}
              </div>
              {errors.Title && (
                <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                  {errors.Title.message}
                </p>
              )}
            </div>

            {/* Details / Content */}
            <div className="col-span-1 md:col-span-2 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Notice Content / Details <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute top-3 left-3.5 pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaAlignLeft size={13} />
                </div>
                <textarea
                  {...register("Content", { required: "Notice content is required" })}
                  rows="4"
                  className={`w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border ${
                    errors.Content
                      ? "border-rose-500 bg-rose-50/50"
                      : "border-gray-200 dark:border-slate-700 focus:border-blue-600 dark:focus:border-blue-500"
                  } rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-semibold text-gray-900 dark:text-white transition-all resize-none`}
                  placeholder="Write the full announcement text, instructions, or agenda details here..."
                />
              </div>
              {errors.Content && (
                <p className="text-xs text-rose-500 font-bold ml-1 animate-pulse">
                  {errors.Content.message}
                </p>
              )}
            </div>

            {/* Target Audience */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Target Audience <span className="text-rose-500">*</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaUsers size={14} />
                </div>
                <select
                  {...register("TargetAudience")}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 focus:border-blue-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all cursor-pointer"
                >
                  <option value="All">Everyone (All)</option>
                  <option value="Student">Students Only</option>
                  <option value="Staff">Staff Only</option>
                  <option value="Parent">Parents Only</option>
                </select>
              </div>
            </div>

            {/* Delivered By */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Delivered By / Authority
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaUserTie size={14} />
                </div>
                <select
                  {...register("DeliveredBy")}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 focus:border-blue-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all cursor-pointer"
                >
                  <option value="School Administration">School Administration</option>
                  <option value="Teachers">Teachers / Faculty</option>
                  <option value="Head Boy">Head Boy</option>
                  <option value="Head Girl">Head Girl</option>
                </select>
              </div>
            </div>

            {/* Schedule Date */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Publish / Schedule Date
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaCalendarAlt size={14} />
                </div>
                <input
                  type="date"
                  {...register("ScheduleDate")}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 focus:border-blue-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-xs font-bold text-gray-900 dark:text-white transition-all"
                />
              </div>
            </div>

            {/* Attachment File */}
            <div className="col-span-1 space-y-1.5">
              <label className="block text-xs font-black uppercase tracking-wider text-gray-700 dark:text-slate-300">
                Attachment Document <span className="text-gray-400 font-normal">(PDF, DOCX, Images)</span>
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-600 transition-colors">
                  <FaPaperclip size={13} />
                </div>
                <input
                  type="file"
                  {...register("AttachmentDocument")}
                  className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 rounded-xl text-xs text-gray-700 dark:text-slate-300 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[11px] file:font-bold file:bg-blue-100 file:text-blue-700 dark:file:bg-blue-950 dark:file:text-blue-300 cursor-pointer"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
            </div>
          </div>

          {/* Mark as Important & Active Toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            {/* Mark as Important Switch */}
            <button
              type="button"
              onClick={() => setValue("IsImportant", !isImportantValue)}
              className={`flex items-center justify-between p-3.5 rounded-2xl border transition text-left cursor-pointer ${
                isImportantValue
                  ? "bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-700/60"
                  : "bg-slate-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700/70"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div className={`p-2 rounded-xl ${isImportantValue ? "bg-amber-500 text-white" : "bg-gray-200 dark:bg-slate-700 text-gray-500"}`}>
                  <FaStar size={12} />
                </div>
                <div>
                  <span className="text-xs font-black text-gray-900 dark:text-white block">
                    High Priority Notice
                  </span>
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                    Highlights with warning badge
                  </span>
                </div>
              </div>
              <div className={`w-5 h-5 rounded-md border flex items-center justify-center text-xs ${isImportantValue ? "bg-amber-500 border-amber-500 text-white" : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900"}`}>
                {isImportantValue && <FaCheck size={10} />}
              </div>
            </button>

            {/* Active Status (Disabled / Enabled by Default) */}
            <div className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-gray-200 dark:border-slate-700/70">
              <div>
                <span className="text-xs font-black text-gray-900 dark:text-white block">
                  Notice Active Status
                </span>
                <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                  Published by default
                </span>
              </div>

              <button
                type="button"
                disabled={true}
                title="Notice active status is enabled by default"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-black text-xs opacity-75 cursor-not-allowed bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
              >
                <FaToggleOn size={16} />
                <span>Active</span>
              </button>
            </div>
          </div>

          {/* Action Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-gray-100 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-xl transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-xs font-black text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md shadow-blue-500/20 disabled:opacity-50 transition flex items-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <FaCheck size={11} /> {isEdit ? "Update Notice" : "Post Notice"}
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
